import { PLUNDER } from '@somnhold/content';
import { hexToWorld } from '../hex.ts';
import { dist2, idiv, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, Plunder, PlunderCache } from '../types.ts';
import { ev } from './combat.ts';
import { spawnPoint } from './create.ts';
import { stepToward } from './enemies.ts';

/*
 * Plunder incursions (docs/MISIONES.md). The old garrison's caches lie across the clearing; the cart waits
 * in the middle, where an extraction's node would be. A hero picks up a chest and walks it to the cart (it
 * loads itself once close); a coffer moves only with two holding it. What lies on the ground, draggers haul
 * back to their holes. The group leaves when every hero still standing is by the cart; the golem leaves on
 * its own at the end. Everyone down at once, or the cart broken, and only half the load comes home.
 */

const P = PLUNDER;
const standing = (h: Hero): boolean => h.downTicks === 0;
/** The cart stands where an extraction's node would. */
const CART = { x: 0, y: 0 };

export const cacheWorth = (c: PlunderCache): number => (c.kind === 'coffer' ? P.cofferValue : P.chestValue);

/** Late in the run (from `PLUNDER.lateWave`): every cache loaded is worth more. */
export const plunderLate = (s: BattleState): boolean => s.waveT >= P.firstWave + (P.lateWave - 1) * P.waveEvery;

export const newPlunder = (s: BattleState, caches: { kind: 'chest' | 'coffer'; q: number; r: number }[]): Plunder => ({
  caches: caches.map((c) => {
    const w = hexToWorld(c.q, c.r);
    return { id: s.nextId++, kind: c.kind, x: w.x, y: w.y, carriers: [], dragger: 0 };
  }),
  loaded: 0,
  lost: 0,
});

const cacheOf = (p: Plunder, id: number | undefined): PlunderCache | undefined => (id === undefined ? undefined : p.caches.find((c) => c.id === id));

const release = (p: Plunder, h: Hero): void => {
  const c = cacheOf(p, h.carry);
  h.carry = undefined;
  if (c) c.carriers = c.carriers.filter((id) => id !== h.id);
};

/** The carry command: put down what is in hand, or take the nearest cache at hand (from a dragger too). */
export const plunderCarry = (s: BattleState, h: Hero): void => {
  const p = s.plunder;
  if (!p || !standing(h)) return;
  if (h.carry !== undefined) {
    release(p, h);
    ev(s, { t: 'skill', hero: h.id, skill: 'plunderDrop', x: h.x, y: h.y });
    return;
  }
  let best: PlunderCache | undefined;
  let bd = P.reach * P.reach + 1;
  for (const c of p.caches) {
    if (c.carriers.length >= (c.kind === 'coffer' ? 2 : 1)) continue;
    const dd = dist2(h.x, h.y, c.x, c.y);
    if (dd < bd) {
      bd = dd;
      best = c;
    }
  }
  if (!best) return;
  best.dragger = 0;
  best.carriers.push(h.id);
  h.carry = best.id;
  h.target = 0;
  ev(s, { t: 'skill', hero: h.id, skill: 'plunderPick', x: best.x, y: best.y });
};

/** A carrier's pace: slower with a chest, slowest under a coffer. */
export const plunderCarryBp = (s: BattleState, h: Hero): number => {
  const c = s.plunder ? cacheOf(s.plunder, h.carry) : undefined;
  if (!c) return 10000;
  return c.kind === 'coffer' ? P.cofferSpeedBp : P.chestSpeedBp;
};

/** Heroes who decide when to leave: the ones still standing that a player (or a balance bot) moves. */
const deciders = (s: BattleState): Hero[] => s.heroes.filter((h) => standing(h) && h.control !== 'ghost');

/** Whether every hero who decides is by the cart: then the group may leave. */
export const plunderCanLeave = (s: BattleState): boolean => {
  const up = deciders(s);
  return up.length > 0 && up.every((h) => withinRange(h.x, h.y, CART.x, CART.y, P.leaveRange));
};

/** The leave command: home with what is on the cart, if everyone is by it. */
export const plunderLeave = (s: BattleState, h: Hero): void => {
  const p = s.plunder;
  if (!p || p.end || !standing(h) || !plunderCanLeave(s)) return;
  p.end = 'left';
  ev(s, { t: 'skill', hero: h.id, skill: 'plunderLeave', x: CART.x, y: CART.y });
};

/** Caches move with their carriers (a coffer only with two, and it keeps them close) or behind a dragger; near the cart they load. */
export const plunderTick = (s: BattleState): void => {
  const p = s.plunder;
  if (!p || p.end) return;
  // A fallen hero lets go of what they held.
  for (const h of s.heroes) if (!standing(h) && h.carry !== undefined) release(p, h);
  for (const c of [...p.caches]) {
    const hs = c.carriers.map((id) => s.heroes.find((h) => h.id === id)).filter((h): h is Hero => !!h);
    if (hs.length) {
      if (c.kind === 'chest') {
        const h = hs[0] as Hero;
        c.x = h.x;
        c.y = h.y;
      } else {
        if (hs.length >= 2) {
          let sx = 0;
          let sy = 0;
          for (const h of hs) {
            sx += h.x;
            sy += h.y;
          }
          const o = { x: c.x, y: c.y, facing: 0 };
          stepToward(o, idiv(sx, hs.length), idiv(sy, hs.length), idiv(mulBp(hs[0]?.stats.speed ?? 1500, P.cofferSpeedBp), 12));
          c.x = o.x;
          c.y = o.y;
        }
        for (const h of hs) {
          const d2 = dist2(h.x, h.y, c.x, c.y);
          if (d2 <= P.cofferLeash * P.cofferLeash) continue;
          const d = isqrt(d2) || 1;
          h.x = c.x + idiv((h.x - c.x) * P.cofferLeash, d);
          h.y = c.y + idiv((h.y - c.y) * P.cofferLeash, d);
        }
      }
      if (withinRange(c.x, c.y, CART.x, CART.y, P.cartRange)) {
        for (const h of hs) h.carry = undefined;
        p.caches = p.caches.filter((o) => o !== c);
        p.loaded += cacheWorth(c) + (plunderLate(s) ? P.lateBonus : 0);
        ev(s, { t: 'skill', hero: (hs[0] as Hero).id, skill: 'plunderLoad', x: CART.x, y: CART.y });
      }
      continue;
    }
    // A dragger that fell lets go where it was.
    if (c.dragger && !s.enemies.some((e) => e.id === c.dragger && e.hp > 0)) c.dragger = 0;
  }
};

/** A dragger: to the nearest cache lying free, then back to its hole with it; it never fights. */
export const draggerTick = (s: BattleState, e: Enemy, px: number): void => {
  const p = s.plunder;
  if (!p) return;
  const held = p.caches.find((c) => c.dragger === e.id);
  if (held) {
    const hole = spawnPoint(s, e.sector);
    e.anim = 'walk';
    if (stepToward(e, hole.x, hole.y, mulBp(px, P.dragSpeedBp))) {
      p.caches = p.caches.filter((c) => c !== held);
      p.lost += cacheWorth(held);
      ev(s, { t: 'boom', x: e.x, y: e.y, r: 600, kind: 'plunderLost' });
      return;
    }
    held.x = e.x;
    held.y = e.y;
    return;
  }
  let best: PlunderCache | undefined;
  let bd = 0x7fffffff;
  for (const c of p.caches) {
    if (c.carriers.length || c.dragger) continue;
    const dd = dist2(e.x, e.y, c.x, c.y);
    if (dd < bd) {
      bd = dd;
      best = c;
    }
  }
  if (!best) {
    e.anim = 'idle';
    return;
  }
  e.anim = 'walk';
  if (stepToward(e, best.x, best.y, px)) best.dragger = e.id;
};

/** How a plunder ends: left (by choice or the golem's), or fallen (everyone down, or the cart broken). */
export const plunderResult = (s: BattleState): 'won' | 'lost' | null => {
  const p = s.plunder;
  if (!p) return null;
  if (!p.end) {
    if ((s.objective && s.objective.hp <= 0) || (s.heroes.length && s.heroes.every((h) => !standing(h)))) p.end = 'fallen';
    else if (s.tick >= P.maxTicks) p.end = 'left';
  }
  if (!p.end) return null;
  return p.end === 'left' && p.loaded >= P.winLoad ? 'won' : 'lost';
};

/** What a plunder pays, as a share of a plain extraction (bp): the load, halved if they fell, capped. */
export const plunderShareBp = (p: Plunder): number => {
  const paid = p.end === 'fallen' ? mulBp(p.loaded * 10000, P.fallenBp) : p.loaded * 10000;
  return imin(P.capBp, idiv(paid, P.fullLoad));
};
